import React, { useEffect, useRef, useState } from "react";

function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
      },
      { threshold, rootMargin: "0px 0px -20% 0px" }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, [threshold]);
  return [ref, visible];
}

export default function TrustedClients() {
  const [headingRef, headingVisible] = useReveal(0.2);
  const [logosRef, logosVisible] = useReveal(0.1);
  const [statsRef, statsVisible] = useReveal(0.15);

  const companies = [
    {
      name: "Company 1",
      logo: "https://tse2.mm.bing.net/th/id/OIP.iI0JaCFWdxDi0eQz3SoRCAHaGP?rs=1&pid=ImgDetMain&o=7&rm=3",
    },
    { name: "Company 2", logo: "https://cdn.dotpe.in/logo/12518/ENT00843.png" },
    {
      name: "Company 3",
      logo: "https://th.bing.com/th/id/OIP.RFsunesO1GXHYYssG9fWbAHaHa?w=175&h=180&c=7&r=0&o=7&pid=1.7&rm=3",
    },
  ];

  const stats = [
    { value: "10K+", label: "Active Businesses" },
    { value: "2M+", label: "Orders Processed" },
    { value: "99.9%", label: "Platform Uptime" },
    { value: "4.9 ★", label: "Average Rating" },
  ];

  return (
    <section className="relative bg-white py-20 overflow-hidden border-t border-gray-100">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50/60 to-white" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Heading ──────────────────────────────────────────── */}
        <div
          ref={headingRef}
          className={`text-center mb-12 reveal-up reveal-slow ${headingVisible ? "revealed" : ""}`}
        >
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-gray-400 mb-4">
            <span className="block w-8 h-px bg-gray-300" />
            Trusted by growing businesses
            <span className="block w-8 h-px bg-gray-300" />
          </span>
          <h2
            className="text-3xl lg:text-4xl font-extrabold text-gray-950 tracking-tight"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Our Trusted{" "}
            <span className="bg-gradient-to-r from-teal-600 to-emerald-500 bg-clip-text text-transparent">
              Partners
            </span>
          </h2>
        </div>

        {/* ── Scrolling logos ───────────────────────────────────── */}
        <div
          ref={logosRef}
          className={`reveal-fade reveal-delay-1 ${logosVisible ? "revealed" : ""}`}
        >
          <div className="relative">
            {/* Edge fades */}
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-r from-white to-transparent" />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-l from-white to-transparent" />

            <div className="flex overflow-hidden">
              <div className="flex animate-scroll gap-0">
                {[...companies, ...companies, ...companies].map((company, index) => (
                  <div
                    key={`a-${index}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center"
                    style={{ width: "180px", height: "80px" }}
                  >
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="max-w-full max-h-full object-contain transition-all duration-300"
                    />
                  </div>
                ))}
              </div>
              <div className="flex animate-scroll gap-0" aria-hidden>
                {[...companies, ...companies, ...companies].map((company, index) => (
                  <div
                    key={`b-${index}`}
                    className="flex-shrink-0 mx-8 flex items-center justify-center"
                    style={{ width: "180px", height: "80px" }}
                  >
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 opacity-50 hover:opacity-100 transition-all duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Trust stat bar ────────────────────────────────────── */}
        <div
          ref={statsRef}
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 rounded-2xl overflow-hidden border border-gray-100 shadow-sm"
        >
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`flex flex-col items-center justify-center py-6 bg-white hover:bg-teal-50/30 transition-colors duration-200 reveal-scale reveal-delay-${i} ${statsVisible ? "revealed" : ""}`}
            >
              <p className="text-2xl font-black text-gray-900 tracking-tight">{stat.value}</p>
              <p className="text-xs text-gray-400 mt-1 font-medium text-center">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 35s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}
